/**
 * SpacesPage — 스페이스 목록
 *
 * 팀·파일·AI 에이전트를 하나의 협업 공간으로 묶는 Space 기능의 목록 페이지.
 * 현재는 UI 목업(하드코딩 데이터)으로 구현되어 있으며, 실제 API 연동 예정.
 */

import { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Topbar } from '../components/layout';
import { SearchInput, Button, Modal, Select, useToast } from '../components/ui';
import spacesApi, { Space } from '../api/spaces';
import { workflowApi, Workflow } from '../api';
import { authApi, User } from '../api';
import './SpacesPage.css';

// ── 타입 ──────────────────────────────────────────────────────────────────────

type SpaceStatus = 'active' | 'pending';

interface SpaceMember {
  initial: string;
  color: string;
}

interface SpaceItem {
  id: string;
  emoji: string;
  iconBg: string;
  name: string;
  desc: string;
  status: SpaceStatus;
  agentTags: string[];
  fileCount: number;
  members: SpaceMember[];
  extraMembers: number;
  chatCount: number;
  cat: string;
}

// ── 카테고리 ──────────────────────────────────────────────────────────────────

const CATS = [
  { id: 'all',    label: '전체' },
  { id: 'mine',   label: '내가 만든' },
  { id: 'shared', label: '나와 공유됨' },
];

type ModalStep = 'name' | 'workflow' | 'member' | null;

// ── 컴포넌트 ──────────────────────────────────────────────────────────────────

export function SpacesPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const currentUser = authApi.getUser();

  const [spaces, setSpaces] = useState<Space[]>([]);
  const [loadingSpaces, setLoadingSpaces] = useState(true);
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [systemUsers, setSystemUsers] = useState<User[]>([]);

  const [cat,    setCat]    = useState('all');
  const [search, setSearch] = useState('');
  const [modalStep, setModalStep] = useState<ModalStep>(null);
  const [creating, setCreating] = useState(false);

  // 생성 모달 상태
  const [newName,    setNewName]    = useState('');
  const [newDesc,    setNewDesc]    = useState('');
  const [newEmoji,   setNewEmoji]   = useState('📝');
  const [selWfId,    setSelWfId]    = useState<string>('');
  const [memberSearch, setMemberSearch] = useState('');
  const [selEmails, setSelEmails]   = useState<string[]>([]);
  const [emailInput, setEmailInput] = useState('');

  // 스페이스 로드
  useEffect(() => {
    spacesApi.list().then(setSpaces).catch(() => {}).finally(() => setLoadingSpaces(false));
  }, []);

  // 워크플로우 로드 (배포된 KB(문서검색) 워크플로우만)
  useEffect(() => {
    workflowApi.list({ deployed: true })
      .then(wfs => setWorkflows(wfs.filter(w => w.nodes.some(n => n.type === 'knowledge-retrieval'))))
      .catch(() => {});
    // 사용자 목록 로드 (멤버 초대용 — 내 범위 내 사용자)
    authApi.listPeers().then(setSystemUsers).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    return spaces.filter((s) => {
      if (cat === 'mine' && s.userId !== currentUser?.email) return false;
      if (cat === 'shared' && s.userId === currentUser?.email) return false;
      if (search) {
        const q = search.toLowerCase();
        return s.spaceName.toLowerCase().includes(q) || (s.description || '').toLowerCase().includes(q);
      }
      return true;
    });
  }, [spaces, cat, search, currentUser?.id]);

  const catCounts = useMemo(() => ({
    all:    spaces.length,
    mine:   spaces.filter(s => s.userId === currentUser?.email).length,
    shared: spaces.filter(s => s.userId !== currentUser?.email).length,
  }), [spaces, currentUser?.id]);

  const openCreate = () => {
    setNewName(''); setNewDesc('');
    setSelWfId(workflows[0]?.id || '');
    setSelEmails([]); setMemberSearch(''); setEmailInput('');
    setModalStep('name');
  };

  const handleCreate = async () => {
    if (!newName.trim() || !selWfId) return;
    setCreating(true);
    try {
      const created = await spacesApi.create({
        spaceName: newName.trim(),
        description: newDesc.trim(),
        workflowId: selWfId,
        inviteEmails: selEmails,
      });
      setSpaces(prev => [created, ...prev]);
      setModalStep(null);
      showToast('success', `"${created.spaceName}" 스페이스가 생성됐습니다.`);
      navigate(`/spaces/${created.spaceId}`);
    } catch {
      showToast('error', '스페이스 생성에 실패했습니다.');
    } finally {
      setCreating(false);
    }
  };

  const handleCardClick = (space: Space) => navigate(`/spaces/${space.spaceId}`);

  // ── 렌더 ────────────────────────────────────────────────────────────────────

  return (
    <>
      <Topbar
        title="스페이스"
        subtitle="팀·파일·AI 에이전트를 하나의 협업 공간으로"
        actions={
          <div className="spaces-topbar-actions">
            <SearchInput
              placeholder="스페이스 이름, 에이전트로 검색"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Button color="primary" onClick={openCreate}>
              + 새 스페이스
            </Button>
          </div>
        }
      />

      <div className="page-content">
        <div className="page-content-inner spaces-container">

          {/* 툴바 */}
          <div className="spaces-toolbar">
            <div className="spaces-chips" role="group" aria-label="카테고리">
              {CATS.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  aria-pressed={cat === c.id}
                  className={`spaces-chip${cat === c.id ? ' spaces-chip--active' : ''}`}
                  onClick={() => setCat(c.id)}
                >
                  {c.label}
                  <span className="spaces-chip-count">
                    {catCounts[c.id as keyof typeof catCounts] ?? 0}
                  </span>
                </button>
              ))}
            </div>
            <div className="spaces-toolbar-right">
              <span className="spaces-count">{filtered.length}개 스페이스</span>
            </div>
          </div>

          {/* 그리드 */}
          {loadingSpaces ? (
            <div className="spaces-empty"><p>로딩 중...</p></div>
          ) : filtered.length > 0 ? (
            <div className="spaces-grid">
              {filtered.map((space) => (
                <article
                  key={space.spaceId}
                  className="space-card"
                  onClick={() => handleCardClick(space)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleCardClick(space); }}
                >
                  <div className="space-card-body">
                    <h2 className="space-card-name">{space.spaceName}</h2>
                    <p className="space-card-desc">{space.description || '설명 없음'}</p>
                  </div>
                  <div className="space-card-tags">
                    <span className="space-tag">파일 {space.fileCount}개</span>
                    <span className="space-tag">멤버 {space.members.length}명</span>
                  </div>
                  <div className="space-card-footer">
                    <div className="space-card-creator">
                      <span className="space-card-creator-name">{space.userName}</span>
                      <span className="space-card-creator-date">
                        {new Date(space.createdAt).toLocaleDateString('ko-KR')}
                      </span>
                    </div>
                  </div>
                </article>
              ))}

              {/* 새 스페이스 추가 카드 */}
              <div
                className="space-card-add"
                role="button"
                tabIndex={0}
                onClick={openCreate}
                onKeyDown={(e) => { if (e.key === 'Enter') openCreate(); }}
              >
                <div className="space-card-add-icon">＋</div>
                <div className="space-card-add-title">새 스페이스 만들기</div>
                <div className="space-card-add-sub">
                  워크플로우·파일·팀을<br />하나의 공간에 모으세요
                </div>
              </div>
            </div>
          ) : (
            <div className="spaces-empty">
              <p>검색 결과가 없습니다.</p>
              <p>다른 키워드나 카테고리로 검색해보세요.</p>
            </div>
          )}
        </div>
      </div>

      {/* ══ 생성 모달: Step 1 — 기본 정보 ══ */}
      {modalStep === 'name' && (
        <Modal
          isOpen
          title="새 스페이스 만들기"
          className="space-create-modal"
          onClose={() => setModalStep(null)}
          footerRight={
            <>
              <Button appearance="outline" color="gray" onClick={() => setModalStep(null)}>취소</Button>
              <Button color="primary" disabled={!newName.trim()} onClick={() => setModalStep('workflow')}>다음 →</Button>
            </>
          }
        >
          <div className="form-grid">
            <div className="form-item">
              <div className="form-label">스페이스 이름 <span className="required">*</span></div>
              <div className="input">
                <input className="input-control" type="text" placeholder="예: 계약서 검토 팀, R&D 리서치"
                  value={newName} onChange={(e) => setNewName(e.target.value)} />
              </div>
            </div>
            <div className="form-item">
              <div className="form-label">설명</div>
              <div className="input">
                <input className="input-control" type="text" placeholder="이 스페이스의 목적을 간략히 적어주세요"
                  value={newDesc} onChange={(e) => setNewDesc(e.target.value)} />
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* ══ 생성 모달: Step 2 — 워크플로우 선택 ══ */}
      {modalStep === 'workflow' && (
        <Modal
          isOpen
          title="새 스페이스 만들기"
          className="space-create-modal"
          onClose={() => setModalStep(null)}
          footerRight={
            <>
              <Button appearance="outline" color="gray" onClick={() => setModalStep('name')}>← 이전</Button>
              <Button color="primary" disabled={!selWfId} onClick={() => setModalStep('member')}>다음 →</Button>
            </>
          }
        >
          <div className="form-item">
            <div className="form-label">챗봇 선택 <span className="required">*</span></div>
            {workflows.length === 0 ? (
              <p className="sp-hint">배포된 KB 워크플로우가 없습니다. 문서 검색 노드가 포함된 워크플로우를 먼저 배포해주세요.</p>
            ) : (
              <Select value={selWfId} onChange={(e) => setSelWfId(e.target.value)}>
                <option value="">선택하세요</option>
                {workflows.map((wf) => (
                  <option key={wf.id} value={wf.id}>{wf.name}</option>
                ))}
              </Select>
            )}
            <p className="sp-desc">
              이 스페이스에서 사용할 AI 챗봇(워크플로우)을 선택하세요.<br />
              <strong>문서 검색(지식베이스)</strong> 노드가 포함된 워크플로우만 표시됩니다.
            </p>
          </div>
        </Modal>
      )}

      {/* ══ 생성 모달: Step 3 — 멤버 초대 ══ */}
      {modalStep === 'member' && (
        <Modal
          isOpen
          title="새 스페이스 만들기"
          className="space-create-modal"
          onClose={() => setModalStep(null)}
          footerRight={
            <>
              <Button appearance="outline" color="gray" onClick={() => setModalStep('workflow')}>← 이전</Button>
              <Button appearance="outline" color="primary" onClick={handleCreate} disabled={creating}>건너뛰기</Button>
              <Button color="primary" onClick={handleCreate} disabled={creating}>
                {creating ? '생성 중...' : '스페이스 생성 ✓'}
              </Button>
            </>
          }
        >
          <div className="form-grid">
            <div className="form-item">
              <div className="form-label">멤버 추가</div>
              {(() => {
                const currentEmail = authApi.getUser()?.email;
                const available = systemUsers.filter(
                  u => u.email !== currentEmail && !selEmails.includes(u.email)
                );
                const [userSearch, setUserSearch] = [emailInput, setEmailInput];
                const filteredUsers = userSearch.trim()
                  ? available.filter(u =>
                      u.name.toLowerCase().includes(userSearch.toLowerCase()) ||
                      u.email.toLowerCase().includes(userSearch.toLowerCase())
                    )
                  : available;
                const dropOpen = memberSearch === 'open';
                const setDropOpen = (v: boolean) => setMemberSearch(v ? 'open' : '');
                return (
                  <div className="sm-wrap">
                    <div className="input sm-trigger" onClick={() => setDropOpen(true)}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--text-3)" strokeWidth="2">
                        <circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                      </svg>
                      <input
                        className="input-control sm-input"
                        placeholder="이름 또는 이메일 검색..."
                        value={userSearch}
                        onChange={(e) => { setUserSearch(e.target.value); setDropOpen(true); }}
                        onFocus={() => setDropOpen(true)}
                        onBlur={() => setTimeout(() => setDropOpen(false), 150)}
                      />
                    </div>
                    {dropOpen && (
                      <div className="sm-dropdown">
                        {filteredUsers.length === 0 ? (
                          <div className="sm-empty">검색 결과 없음</div>
                        ) : filteredUsers.map(u => (
                          <div key={u.email} className="sm-item"
                            onMouseDown={() => { setSelEmails(prev => [...prev, u.email]); setUserSearch(''); setDropOpen(false); }}>
                            <div className="sm-av">{u.name[0]}</div>
                            <div>
                              <div className="sm-name">{u.name}</div>
                              <div className="sm-email">{u.email}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>

            <div className="form-item sp-mem-section">
              <div className="form-label">초대 목록</div>
              <div className="space-mem-list">
                <div className="space-mem-row">
                  <div className="space-mem-av space-mem-av--owner">{(authApi.getUser()?.name || 'U')[0]}</div>
                  <div className="sp-mem-info">
                    <div className="space-mem-name">{authApi.getUser()?.name || ''} (나)</div>
                    <div className="space-mem-email">{authApi.getUser()?.email}</div>
                  </div>
                  <span className="space-role-badge">소유자</span>
                </div>
                {selEmails.map((email) => {
                  const user = systemUsers.find(u => u.email === email);
                  return (
                    <div key={email} className="space-mem-row">
                      <div className="space-mem-av space-mem-av--invited">{(user?.name || email)[0].toUpperCase()}</div>
                      <div className="sp-mem-info">
                        {user?.name && <div className="space-mem-name">{user.name}</div>}
                        <div className="space-mem-email">{email}</div>
                      </div>
                      <button
                        className="sp-mem-rm"
                        onClick={() => setSelEmails(prev => prev.filter(e => e !== email))}
                      >✕</button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <p className="sp-note">
            ※ 멤버 초대는 나중에도 가능합니다. 건너뛰기를 눌러 먼저 생성할 수 있습니다.
          </p>
        </Modal>
      )}
    </>
  );
}

export default SpacesPage;
